import React from "react";
import { Form } from "react-final-form";
import { Checkbox } from "../../../../../common/components/input/Checkbox";
import { Text } from "../../../../../common/components/input/Text";
import { SettingRow } from "./SettingRow";
import "../../../../../../sass/components/inputs/custom-text-width.scss";

export enum CustomFieldEnabledType {
  OPT_OUT = "OPT_OUT",
  REQUEST_FORM = "REQUEST_FORM",
  OPT_OUT_CONFIRMATION_MESSAGE = "OPT_OUT_CONFIRMATION_MESSAGE",
}

export interface PrivacyCenterRequestFormSettingsRowProps {
  title?: string;
  handleFormSubmit: (values: any, form: any) => void;
  customFieldEnabled: boolean;
  customFieldEnabledType: CustomFieldEnabledType;
  customFieldHelp: string;
  customFieldLabel: string;
  optOutConfirmationMessage?: string;
  showOptOutConfirmationMessageField?: boolean;
  extendedAddressFormKey?:
    | "showExtendedAddressFormForOptOut"
    | "showExtendedAddressFormForRequests"; // This component can be linked to either of these columns
  extendedAddressFormValue?: boolean;
  showExtendedAddressForm?: boolean;
  phoneNumberFormKey?: "showPhoneNumberFormForOptOut" | "showPhoneNumberFormForRequests"; // This component can be linked to either of these columns
  phoneNumberFormValue?: boolean;
  showPhoneNumberForm?: boolean;
  firstLastNameRequiredKey?: "requireFirstLastNameOnYpcPage";
  firstLastNameRequiredValue?: boolean;
  showFirstLastNameRequiredField?: boolean;
  showCustomField?: boolean;
  otherFormsSubmitting?: boolean;
}

export const PrivacyCenterRequestFormSettingsRow = ({
  title = "Request Form",
  handleFormSubmit,
  customFieldEnabled,
  customFieldHelp,
  customFieldLabel,
  customFieldEnabledType,
  showOptOutConfirmationMessageField,
  extendedAddressFormKey,
  extendedAddressFormValue,
  showExtendedAddressForm,
  phoneNumberFormKey,
  phoneNumberFormValue,
  showPhoneNumberForm,
  firstLastNameRequiredKey,
  firstLastNameRequiredValue,
  showFirstLastNameRequiredField,
  showCustomField,
  otherFormsSubmitting,
  optOutConfirmationMessage,
}: PrivacyCenterRequestFormSettingsRowProps) => {
  const helpText = (
    <>
      The default {title.toLowerCase()} asks for name and email; you can request additional
      information by enabling a custom field.{" "}
      <strong>
        Do not collect additional information unless it is needed to process a request.
      </strong>
    </>
  );

  return (
    <SettingRow title={title} helpText={helpText} maxWidthChildren={true}>
      <div className="flex-col">
        {/* Enable Address Form */}
        {showExtendedAddressForm && (
          <Form
            onSubmit={handleFormSubmit}
            initialValues={{
              [extendedAddressFormKey]: extendedAddressFormValue,
            }}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} className="flex-grow">
                <Checkbox
                  field={extendedAddressFormKey}
                  color="primary"
                  label="Enable Address Form"
                  afterChange={handleSubmit}
                  disabled={submitting || otherFormsSubmitting}
                />
              </form>
            )}
          />
        )}

        {/* Enable Phone Number Form */}
        {showPhoneNumberForm && (
          <Form
            onSubmit={handleFormSubmit}
            initialValues={{
              [phoneNumberFormKey]: phoneNumberFormValue,
            }}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} className="flex-grow">
                <Checkbox
                  field={phoneNumberFormKey}
                  color="primary"
                  label="Enable Phone Number Form"
                  afterChange={handleSubmit}
                  disabled={submitting || otherFormsSubmitting}
                />
              </form>
            )}
          />
        )}

        {showFirstLastNameRequiredField && (
          <Form
            onSubmit={handleFormSubmit}
            initialValues={{
              [firstLastNameRequiredKey || "requireFirstLastNameOnYpcPage"]:
                firstLastNameRequiredValue,
            }}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} className="flex-grow">
                <div className="w-445">
                  <Checkbox
                    field={firstLastNameRequiredKey || "requireFirstLastNameOnYpcPage"}
                    color="primary"
                    label="Make &ldquo;First Name&rdquo; and &ldquo;Last Name&rdquo; required on YPC page"
                    className="mb-xxs"
                    afterChange={handleSubmit}
                    disabled={submitting || otherFormsSubmitting}
                  />
                  <div className="text-t1 text-muted">
                    Select this option to require users to enter their first and last name on the
                    request form.
                  </div>
                </div>
              </form>
            )}
          />
        )}

        {/* Enable Custom Field */}
        {showCustomField && (
          <Form
            onSubmit={handleFormSubmit}
            initialValues={{
              [customFieldEnabledType]: customFieldEnabled,
              customFieldHelp: customFieldHelp,
              customFieldLabel: customFieldLabel,
            }}
            render={({ handleSubmit, values, submitting, form }) => (
              <form onSubmit={handleSubmit} className="flex-grow">
                <Checkbox
                  field={customFieldEnabledType}
                  color="primary"
                  label="Enable Custom Field"
                  afterChange={(checked) => {
                    if (!checked) {
                      void handleFormSubmit(
                        {
                          ...values,
                          [customFieldEnabledType]: false,
                        },
                        form,
                      );
                      return;
                    }
                    handleSubmit();
                  }}
                  disabled={submitting || otherFormsSubmitting}
                />
                {values[customFieldEnabledType] && (
                  <>
                    <label htmlFor="customFieldLabel" className="text-t3 mr-md">
                      Text Field Label
                    </label>
                    <Text
                      className="align-middle w-300"
                      fullWidth={false}
                      field="customFieldLabel"
                      required
                      variant="outlined"
                      disabled={submitting || otherFormsSubmitting}
                      onBlur={handleSubmit}
                      validate={(str) => {
                        if (!values[customFieldEnabledType]) {
                          return;
                        }
                        if (!str?.trim()) {
                          return "Required";
                        }
                        if (str.trim().length > 50) {
                          return "Max of 50 Characters";
                        }
                      }}
                    />
                    <label htmlFor="customFieldHelp" className="text-t3 d-block mt-md">
                      Help Text (max 200 characters)
                    </label>
                    <Text
                      className="w-445"
                      fullWidth={false}
                      field="customFieldHelp"
                      multiline
                      rows={4}
                      variant="outlined"
                      disabled={submitting || otherFormsSubmitting}
                      onBlur={handleSubmit}
                      validate={(str) => {
                        if (!values[customFieldEnabledType]) {
                          return;
                        }
                        if (str?.trim()?.length > 200) {
                          return "Max of 200 Characters";
                        }
                      }}
                    />
                  </>
                )}
              </form>
            )}
          />
        )}

        {/* Enable Opt Out Confirmation Message */}
        {showOptOutConfirmationMessageField && (
          <Form
            onSubmit={handleFormSubmit}
            initialValues={{
              [CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE]:
                !!optOutConfirmationMessage?.trim(),
              optOutConfirmationMessage: optOutConfirmationMessage || "",
            }}
            render={({ handleSubmit, values, submitting, form }) => (
              <form onSubmit={handleSubmit} className="flex-grow">
                <Checkbox
                  field={CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE}
                  color="primary"
                  label="Customize the opt-out confirmation message"
                  afterChange={(checked) => {
                    if (!checked) {
                      void handleFormSubmit(
                        {
                          ...values,
                          [CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE]: false,
                        },
                        form,
                      );
                      return;
                    }
                    handleSubmit();
                  }}
                />
                {values[CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE] && (
                  <>
                    <label
                      htmlFor="optOutConfirmationMessage"
                      className="text-t3 d-block mt-md mb-md"
                    >
                      Confirmation Message (max 600 characters)
                    </label>
                    <Text
                      className="w-445"
                      fullWidth={false}
                      field="optOutConfirmationMessage"
                      multiline
                      rows={4}
                      variant="outlined"
                      disabled={submitting || otherFormsSubmitting}
                      onBlur={handleSubmit}
                      validate={(str) => {
                        if (!values[CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE]) {
                          return;
                        }
                        if (!str?.trim()) {
                          return "Required";
                        }
                        if (str?.trim()?.length > 600) {
                          return "Max of 600 Characters";
                        }
                      }}
                    />
                  </>
                )}
              </form>
            )}
          />
        )}
      </div>
    </SettingRow>
  );
};
